/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnChanges,
	SimpleChanges,
	Optional,
} from '@angular/core';
import { UntypedFormControl, UntypedFormArray, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, filter, map, distinctUntilChanged } from 'rxjs/operators';

import { PromisePin, cloneStack, pinState, Variance, TicketType } from '@touchplan/common';
import { TicketEditBaseComponent } from '../ticket-edit-base/ticket-edit-base.component';
import { TicketEditService } from '../../ticket-edit.service';
import * as strings from '@touchplan/strings';
import {
	activeLineSideValidator,
	ActiveLineValidatorConfig,
} from 'src/app/app-data/validators/active-line-side.validator';
import { PlanState } from 'src/app/services/plan-state.service';
import { validatorDecorator } from '../../ticket-errors';

const MAX_HEIGHT = 540;

/** returns error data when the formArray has duplicate plannedFinish values */
//pretty heavy for a validator...
export function sameDateValidator() {
	return (control: UntypedFormArray) => {
		if (!control.value || control.value.length === 0) {
			return null;
		}

		const dateCache: any = {};
		let isDuplicate = false;
		control.value.forEach((data) => {
			if (dateCache[data.plannedFinish]) {
				isDuplicate = true;
			}
			dateCache[data.plannedFinish] = true;
		});

		return isDuplicate
			? {
					sameDateValidator: {
						value: control.value,
						messageHeader: 'test',
						message: strings.PIN_HISTORY_DUPLICATE_DATES,
					},
			  }
			: null;
	};
}

const activeLineValidatorConfig: ActiveLineValidatorConfig = {
	comparisonMethod: 'date',
	comparisonStyle: '<=',
	message: strings.ACTIVE_LINE_PIN_RIGHT,
	messageHeader: strings.ACTIVE_LINE_PIN_RIGHT_HEADER,
};

@Component({
	selector: 'ticket-pin-history',
	templateUrl: './ticket-pin-history.component.html',
	styleUrls: ['./ticket-pin-history.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketPinHistoryComponent extends TicketEditBaseComponent implements OnInit, OnChanges {
	public isEditing = new BehaviorSubject(false);
	public hasExpandedNotes = new BehaviorSubject([]);
	public makeShake = new Subject<void>();

	// public get errorText() {
	// 	if (!this.editForm.errors) { return ''; }
	// 	if(this.editForm.errors.sameDateValidator)
	// 	return this.editForm.errors?.sameDateValidator?.message || "All variance is required"
	// }

	public activeLine$ = this.planState.plan.object$.pipe(
		map((plan) => {
			if (!plan) {
				return null;
			}
			return { activeLineDate: plan.view.activeLineDate } || null;
		}),
		distinctUntilChanged(),
		takeUntil(this.shutdownSubject)
	);

	private initialSnapshot: PromisePin[];

	private ignoreArrayManipulation = false; //hack because there's no "don't emit" setting for adding and removing from a formArray

	private cameraSnapshot;

	constructor(
		@Optional() protected cd: ChangeDetectorRef,
		private planState: PlanState,
		private ticketEditService: TicketEditService
	) {
		super(null, null, cd);
	}

	//angular did it again, for some reason they suck at making an "ok" forms api
	//https://github.com/angular/angular/issues/30264
	public editFormWrapped = new UntypedFormGroup({
		array: new UntypedFormArray([], { validators: sameDateValidator() }),
	});
	public editForm = this.editFormWrapped.get('array') as UntypedFormArray;

	/**
	 * Returns total pins in the provided promiseStack
	 */
	get totalPins(): number {
		return this.promiseStack ? this.promiseStack.length : 0;
	}
	/**
	 * promiseStack - The livePromiseStack data
	 */
	@Input() public promiseStack: PromisePin[] = [];
	@Input() public varianceList$: Observable<Map<string, Variance>>;
	@Input() public isAdmin = false;
	@Input() public hasAccess = false;
	@Input() public ticketDate = '';
	@Input() public isActual = false;
	/**
	 * ticketType - The type of ticket we are displaying pin history for.
	 */
	@Input() public ticketType: TicketType = TicketType.Task;
	@Output() public closed: EventEmitter<any> = new EventEmitter();
	@Output() public saved: EventEmitter<any> = new EventEmitter();
	@Output() public revert: EventEmitter<any> = new EventEmitter();

	public ngOnInit() {
		this.editForm.valueChanges
			.pipe(
				// skipWhile(f => this.ignoreArrayManipulation),
				filter((f) => !this.ignoreArrayManipulation),
				map((pins) => {
					if (!pins) {
						return pins;
					}
					pins.forEach((pin) => {
						if (pin.varianceComment === '') {
							pin.varianceComment = null;
						}
					});
					return pins;
				}),
				takeUntil(this.shutdownSubject)
			)
			.subscribe((val) => {
				this.updateData(val);
			});

		this.cameraSnapshot = this.planState.scopeSoup.hud.snapshotHudPos();

		this.isEditing.pipe(takeUntil(this.shutdownSubject)).subscribe((editing) => {
			if (!editing) {
				this.editForm.markAsPristine();
			}
		});
	}

	public ngOnChanges(changes: SimpleChanges) {
		if (changes['promiseStack']) {
			//save the original one for a cancel
			const promiseStack = changes['promiseStack'];

			//tha first
			if (promiseStack.currentValue && !promiseStack.previousValue) {
				this.initialSnapshot = cloneStack(promiseStack.currentValue);
			}

			//go with workflow where external updates nuke local ones
			this.correctFormArrayLength(promiseStack.currentValue);
			this.updateForm(promiseStack.currentValue);
		}
	}

	private updateForm(arr: PromisePin[]) {
		if (!arr) {
			return;
		}
		const expandedNotes = this.hasExpandedNotes.getValue();
		let hasChangedExpanded = false;

		const trimmed = arr.map((pin, i) => {
			if (pin.data.varianceComment && !expandedNotes[i]) {
				expandedNotes[i] = true;
				hasChangedExpanded = true;
			}
			return {
				id: pin.$id,
				plannedFinish: pin.data.plannedFinish,
				varianceId: pin.data.varianceId,
				varianceComment: pin.data.varianceComment,
			};
		});

		if (hasChangedExpanded) {
			this.hasExpandedNotes.next(expandedNotes);
		}

		this.editForm.patchValue(trimmed, { emitEvent: false });

		//update the disabled state of the variance reason picker
		// angular is kind of a dick and does let you do this [disabled]="blah"
		// without a ton of errors when reactive forms are used so... this
		arr.forEach((pin, i, list) => {
			const varianceControl = this.editForm.controls[i].get('varianceId');
			const varianceComment = this.editForm.controls[i].get('varianceComment');

			const noVarianceNeeded =
				pin.data.state === pinState.promised || pin.data.state === pinState.completedOnTime;

			if (noVarianceNeeded && varianceControl.enabled) {
				varianceControl.disable();
				varianceComment.disable();
			}
			if (!noVarianceNeeded && varianceControl.disabled) {
				varianceControl.enable();
				varianceComment.enable();
			}

			//see https://mocasystems.atlassian.net/browse/TP-2688 for reasons, can consider turning back on in angular 12
			// const wholeControl = this.editForm.controls[i];
			// if (!this.isAdmin) {
			// 	if (i === list.length - 1 && !wholeControl.enabled) { wholeControl.enable(); }
			// 	else if ( i !== list.length - 1 && !wholeControl.disabled) { wholeControl.disable(); }
			// }
		});
	}

	private createPromiseFormGroup() {
		return new UntypedFormGroup({
			id: new UntypedFormControl(), //unused, just serves as a transfer method
			plannedFinish: new UntypedFormControl(
				null,
				[Validators.required],
				[activeLineSideValidator(this.activeLine$, activeLineValidatorConfig)]
			),
			varianceId: new UntypedFormControl('', [
				validatorDecorator(
					Validators.required,
					strings.EDIT_TOP_BAR_VARIANCE_NEEDED,
					'You must select a variance'
				),
			]),
			varianceComment: new UntypedFormControl(null, { updateOn: 'blur' }),
		});
	}
	private correctFormArrayLength(arr: PromisePin[]) {
		const diff = this.editForm.length - (arr ? arr.length : 0); //in case you delete all promises while the panel is open
		if (diff === 0) {
			return;
		}

		this.ignoreArrayManipulation = true;
		const absDiff = Math.abs(diff);
		for (let i = 0; i < absDiff; i++) {
			if (diff > 0) {
				this.editForm.removeAt(this.editForm.length - 1);
			} else {
				this.editForm.push(this.createPromiseFormGroup());
			}
		}

		this.ignoreArrayManipulation = false;
	}

	private revertToPreviousPos() {
		this.planState.scopeSoup.hud.focusAtPoint(
			this.cameraSnapshot.x,
			this.cameraSnapshot.y,
			this.cameraSnapshot.z
		);
	}

	private getDirty() {
		this.editForm.controls.forEach((c1) => {
			if ((c1 as UntypedFormGroup).controls) {
				// tslint:disable-next-line: forin
				for (const key in (c1 as UntypedFormGroup).controls) {
					(c1 as UntypedFormGroup).controls[key].markAsDirty();
				}
			}
			c1.markAsDirty();
		});
	}

	public get dirtyChildren() {
		const controlsThatCanBeInvalid = ['varianceId'];
		let isDirty = false;

		this.editForm.controls.forEach((c1) => {
			if ((c1 as UntypedFormGroup).controls) {
				controlsThatCanBeInvalid.forEach((key) => {
					isDirty =
						isDirty ||
						((c1 as UntypedFormGroup).controls[key] && (c1 as UntypedFormGroup).controls[key].dirty);
				});
			}
		});

		return isDirty;
	}

	public switchToEdit() {
		this.isEditing.next(true);

		const etc = this.ticketEditService.combinedEdit$.getValue();

		const screenRect = this.planState.canvasConnector.camera.getScreen();
		screenRect.height = screenRect.height - MAX_HEIGHT;

		this.planState.canvasConnector.camera.focusOnTicketsFromEdit(etc, undefined, 0, screenRect);
	}

	public showNote(idx: number) {
		const currentEdit = this.hasExpandedNotes.getValue();
		//TODO - reset
		currentEdit[idx] = true;
		this.hasExpandedNotes.next(currentEdit);
	}

	public cancel() {
		//TODO - maybe make a reset output that takes the Array<PromisePin>?
		// const cleanedData = this.initialSnapshot.map(pin => {
		// 	return {
		// 		id: pin.$id,
		// 		plannedFinish: pin.data.plannedFinish,
		// 		varianceId: pin.data.varianceId,
		// 		varianceComment: pin.data.varianceComment
		// 	};
		// });
		this.revertToPreviousPos();
		this.revert.emit(this.initialSnapshot);
		this.isEditing.next(false);
	}

	public close(skipInvalidCheck = false) {
		if (!skipInvalidCheck && this.editForm.invalid) {
			// this.cd.detectChanges();
			this.getDirty();
			this.makeShake.next();
			return;
		}
		this.revertToPreviousPos();
		this.hasExpandedNotes.next([]); //reset this
		this.closed.emit();
	}

	public deletePin(idx: number) {
		const val = this.editForm.value as any[];
		val.splice(idx, 1);

		this.updateData(val);
	}

	public updateData(val) {
		this.saved.emit(val);
	}
}
